// @ts-nocheck — ported from app/web (noUncheckedIndexedAccess)
/**
 * Chart adapter: renders a StreamAccumulator into Lightweight Charts™.
 *
 * The accumulator is the source of truth; this adapter is a view. On every
 * flush (rAF cadence from the preview-stream store) it applies only the
 * delta since its last flush: new bars via series.update() (O(log n)),
 * fills via markers, plots as line series in an indicators pane. Warmup
 * bars with NaN equity are skipped for the equity series.
 *
 * Times are converted ms → UTC seconds (LWC requirement). NaN plots become
 * whitespace items (line breaks), matching Pine na.
 */

import {
	CandlestickSeries,
	CrosshairMode,
	HistogramSeries,
	LineSeries,
	createChart,
	createSeriesMarkers,
	type IChartApi,
	type ISeriesApi,
	type ISeriesMarkersPluginApi,
	type Time,
	type UTCTimestamp
} from 'lightweight-charts';
import type { StreamAccumulator } from './previewStreamAccumulator';
import { DrawingsPrimitive } from './previewDrawingsPrimitive';

const msToSec = (ms: number): UTCTimestamp => Math.floor(ms / 1000) as UTCTimestamp;

const CANDLE_UP = '#26a69a';
const CANDLE_DOWN = '#ef5350';
const VOLUME_UP = 'rgba(38, 166, 154, 0.5)';
const VOLUME_DOWN = 'rgba(239, 83, 80, 0.5)';
const EQUITY_COLOR = '#2962ff';
const PLOT_COLORS = ['#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6', '#eab308'];

export interface ChartTheme {
	background: string;
	text: string;
	grid: string;
	border: string;
}

/** Legend row: one bar's OHLVC + plot values. */
export interface LegendData {
	/** Accumulator bar index (0-based). -1 when no bars yet. */
	barIndex: number;
	/** Bar open time, unix ms. */
	time: number;
	open: number;
	high: number;
	low: number;
	close: number;
	volume: number;
	/** Close vs open change, percent. */
	changePct: number;
	/** Plot values in meta order; NaN = na. */
	plots: number[];
	/** Plot values the chart series themselves hold at the hovered time
	 *  (NaN = na). Diagnostic: should always equal plots. */
	chartPlots?: number[];
	/** True while the crosshair pins a bar; false = last-bar fallback. */
	hovered: boolean;
	/** Mouse position in pane-relative pixels (tooltip anchor). */
	x?: number;
	y?: number;
	/** Plot titles from meta (stable for the stream's lifetime). */
	plotTitles: string[];
}

export class PreviewChartAdapter {
	private chart: IChartApi | null = null;
	private candles: ISeriesApi<'Candlestick'> | null = null;
	private volume: ISeriesApi<'Histogram'> | null = null;
	private equity: ISeriesApi<'Line'> | null = null;
	private plots: ISeriesApi<'Line'>[] = [];
	private markers: ISeriesMarkersPluginApi<Time> | null = null;
	private drawingsPrim: DrawingsPrimitive | null = null;

	// Legend: crosshair listener reports the hovered bar's data (or the
	// last bar when not hovering). The component renders it as a legend
	// overlay + tooltip; plot names come from meta.
	private legendHandler: ((data: LegendData | null) => void) | null = null;
	private crosshairBound = false;

	// Delta cursors into the accumulator's append-only stores.
	private barsApplied = 0;
	private fillsApplied = 0;
	private markersById = new Map<number, number>(); // fill index → marker count applied

	// Windowed rendering. The accumulator holds the whole history, but the
	// chart is only given bars [winFrom, winTo) (plus the fills, plots and
	// drawings that fall in them): the newest WINDOW_BARS at first. Panning
	// within EDGE_BARS of either end slides the window by CHUNK_BARS, and it
	// never holds more than MAX_BARS, so the cost of a load stays flat however
	// far the user travels. Handing Lightweight Charts hundreds of thousands of
	// bars and thousands of markers in one go is what froze the page.
	static WINDOW_BARS = 1500;
	static CHUNK_BARS = 3000;
	static MAX_BARS = 12000;
	static EDGE_BARS = 60;
	/** Bars shown when a run first renders (the rest of the window is a pan away). */
	static VISIBLE_BARS = 320;
	private winFrom = 0;
	/** Exclusive end; equals the accumulator's bar count while following the newest bars. */
	private winTo = 0;
	private sliding = false;
	private rangeBound = false;

	/** First accumulator bar index currently in the chart. */
	get windowStart(): number {
		return this.winFrom;
	}

	/** One past the last accumulator bar index currently in the chart. */
	get windowEnd(): number {
		return this.winTo;
	}

	// Burst guard: if the replay backlog exceeds this many bars in one
	// flush, collapse to a single setData (one re-layout beats thousands
	// of update() calls on first load).
	private static BURST_THRESHOLD = 5000;

	// The historical replay renders in ONE bulk setData at the live
	// transition. Per-bar update() deltas run only from that point on —
	// rendering thousands of replay bars incrementally is what made the
	// chart feel slow.
	private live = false;

	/** Candles and volume only: no fills, plots or drawings (lighter to render). */
	private candlesOnly = false;

	/** Create the chart inside a container element. */
	mount(el: HTMLElement, theme: ChartTheme, opts: { candlesOnly?: boolean } = {}): void {
		this.destroy();
		this.candlesOnly = Boolean(opts.candlesOnly);
		this.chart = createChart(el, {
			autoSize: true,
			layout: {
				background: { color: theme.background },
				textColor: theme.text,
				attributionLogo: true
			},
			grid: {
				vertLines: { color: theme.grid },
				horzLines: { color: theme.grid }
			},
			crosshair: { mode: CrosshairMode.Normal },
			rightPriceScale: { borderColor: theme.border },
			timeScale: { borderColor: theme.border, timeVisible: true, secondsVisible: false }
		});

		this.candles = this.chart.addSeries(CandlestickSeries, {
			upColor: CANDLE_UP,
			downColor: CANDLE_DOWN,
			borderVisible: false,
			wickUpColor: CANDLE_UP,
			wickDownColor: CANDLE_DOWN
		});
		this.volume = this.chart.addSeries(HistogramSeries, {
			priceFormat: { type: 'volume' },
			priceScaleId: 'vol'
		});
		this.chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

		// Equity pane intentionally omitted — Overview already shows the equity curve.
		this.equity = null;
		this.markers = createSeriesMarkers(this.candles);

		// Crosshair → legend: report the hovered bar (or the last bar on
		// mouse-leave). Subscription stays for the chart's lifetime; the
		// handler is a settable field so the component can rebind freely.
		if (!this.crosshairBound) {
			this.chart.subscribeCrosshairMove((param) => this.onCrosshair(param));
			this.crosshairBound = true;
		}
		if (!this.rangeBound) {
			this.chart.timeScale().subscribeVisibleLogicalRangeChange((range) => this.onVisibleRange(range));
			this.rangeBound = true;
		}
	}

	/** Panning near either end of the window slides it over the history. */
	private onVisibleRange(range: { from: number; to: number } | null): void {
		if (!range || !this.live || this.sliding) return;
		const acc = this.legendAcc;
		if (!acc) return;
		const count = this.winTo - this.winFrom;
		const older = range.from < PreviewChartAdapter.EDGE_BARS && this.winFrom > 0;
		const newer = range.to > count - PreviewChartAdapter.EDGE_BARS && this.winTo < acc.nBars;
		if (!older && !newer) return;
		this.sliding = true;
		// Off the scroll handler's stack, so the pan itself stays smooth.
		setTimeout(() => {
			try {
				this.slide(acc, older ? -1 : 1);
			} finally {
				this.sliding = false;
			}
		}, 0);
	}

	/** Move the window one chunk older (-1) or newer (+1), keeping the same bars on screen. */
	private slide(acc: StreamAccumulator, dir: -1 | 1): void {
		if (!this.chart) return;
		const ts = this.chart.timeScale();
		const before = ts.getVisibleLogicalRange();
		const oldFrom = this.winFrom;
		const { CHUNK_BARS, MAX_BARS } = PreviewChartAdapter;
		if (dir < 0) {
			this.winFrom = Math.max(0, this.winFrom - CHUNK_BARS);
			if (this.winTo - this.winFrom > MAX_BARS) this.winTo = this.winFrom + MAX_BARS;
		} else {
			this.winTo = Math.min(acc.nBars, this.winTo + CHUNK_BARS);
			if (this.winTo - this.winFrom > MAX_BARS) this.winFrom = this.winTo - MAX_BARS;
		}
		this.setData(acc, false);
		this.rebuildMarkers(acc);
		this.barsApplied = acc.nBars;
		// Logical index 0 moved by (oldFrom - winFrom) bars.
		const shift = oldFrom - this.winFrom;
		if (before) ts.setVisibleLogicalRange({ from: before.from + shift, to: before.to + shift });
		this.drawingsPrim?.requestRepaint();
	}

	/** Register the legend consumer (bar data under the crosshair). */
	onLegend(handler: ((data: LegendData | null) => void) | null): void {
		this.legendHandler = handler;
	}

	// State for the crosshair → accumulator lookup: the accumulator is
	// captured per-apply (it is swapped on stream restart).
	private legendAcc: StreamAccumulator | null = null;
	private legendPoint: { x: number; y: number } | null = null;

	private onCrosshair(param: any): void {
		const acc = this.legendAcc;
		if (!acc || !this.legendHandler) return;
		// Not hovering: fall back to the last bar. (The handler renders
		// this too — the legend is always visible once bars flow.)
		if (param.time === undefined || param.point === undefined) {
			this.legendPoint = null;
			this.emitLegend(this.lastLegendBar(acc), false);
			return;
		}
		this.legendPoint = { x: param.point.x, y: param.point.y };
		// The chart's logical index IS the accumulator bar index: bars are
		// appended sequentially (one per closed bar event, no gaps), so
		// setData/update and the accumulator stay index-aligned. Using the
		// logical index directly avoids any time→index mapping mismatch.
		// Logical index 0 is the first bar of the window, not of the run.
		const idx = param.logical === undefined ? -1 : param.logical + this.winFrom;
		if (idx < this.winFrom || idx >= this.winTo || idx >= acc.nBars) return;
		const legend = this.barLegend(acc, idx);
		// Diagnostic: what the chart series themselves hold at the hovered
		// time (whitespace/na = undefined value). When these disagree with
		// the accumulator columns, the append/update path diverged; when
		// they agree, any visual misalignment comes from the wire data.
		legend.chartPlots = this.plots.map((s) => {
			const d = param.seriesData?.get(s);
			return d && typeof d.value === 'number' ? d.value : NaN;
		});
		this.emitLegend(legend, true);
	}

	/** Legend snapshot for accumulator bar idx. */
	private barLegend(acc: StreamAccumulator, idx: number): LegendData {
		const plots: number[] = [];
		for (let p = 0; p < this.plots.length; p++) {
			plots.push(acc.plotColumns[p]?.[idx] ?? NaN);
		}
		const o = acc.bars.open[idx];
		const c = acc.bars.close[idx];
		return {
			barIndex: idx,
			time: acc.bars.time[idx],
			open: o,
			high: acc.bars.high[idx],
			low: acc.bars.low[idx],
			close: c,
			volume: acc.bars.volume[idx],
			changePct: o !== 0 ? ((c - o) / o) * 100 : 0,
			plots,
			hovered: true,
			plotTitles: acc.meta?.plots ?? []
		};
	}

	/** Last-bar legend for the not-hovering state. */
	private lastLegendBar(acc: StreamAccumulator): LegendData {
		if (acc.nBars === 0) {
			return { barIndex: -1, time: 0, open: NaN, high: NaN, low: NaN, close: NaN, volume: NaN, changePct: 0, plots: [], hovered: false, plotTitles: acc.meta?.plots ?? [] };
		}
		const d = this.barLegend(acc, acc.nBars - 1);
		d.hovered = false;
		return d;
	}

	private emitLegend(data: LegendData | null, hovered: boolean): void {
		if (data) {
			data.hovered = hovered;
			if (hovered && this.legendPoint) {
				data.x = this.legendPoint.x;
				data.y = this.legendPoint.y;
			} else {
				delete data.x;
				delete data.y;
			}
		}
		this.legendHandler?.(data);
	}

	/** Attach the drawings primitive (needs the accumulator + series). */
	attachDrawings(acc: StreamAccumulator): void {
		if (!this.candles || this.drawingsPrim) return;
		// LWC v5's attached() provides {chart, requestUpdate} only — the
		// series is handed to the primitive directly.
		this.drawingsPrim = new DrawingsPrimitive(acc, this.candles, () => this.winFrom, () => this.winTo);
		this.candles.attachPrimitive(this.drawingsPrim);
	}

	/** Apply everything new in the accumulator since the last flush. */
	apply(acc: StreamAccumulator): void {
		if (!this.chart || !this.candles) return;

		// The accumulator instance swaps on stream restart — keep the
		// crosshair lookup pointed at the live one.
		this.legendAcc = acc;

		if (!this.candlesOnly) {
			this.applyPanes(acc);
			this.applyDrawings(acc);
		}

		if (!this.live) {
			// During the historical replay: render nothing. The
			// accumulator keeps everything; the chart loads it all in one
			// bulk setData at the live transition. (If the stream ends
			// before going live, finishRender covers it — the modal's
			// stream-ended flush still calls apply.)
			if (acc.phase === 'live' || acc.phase === 'ended' || acc.phase === 'failed') {
				this.finishRender(acc);
			}
			return;
		}

		// Live mode: incremental deltas only — and only while the window follows
		// the newest bars. Parked on older history, new bars just accumulate.
		const pending = acc.nBars - this.barsApplied;
		if (pending > 0) {
			const following = this.winTo === this.barsApplied;
			if (following) {
				if (pending > PreviewChartAdapter.BURST_THRESHOLD) {
					this.winTo = acc.nBars;
					this.setData(acc, false);
				} else {
					this.appendBars(acc, this.barsApplied, acc.nBars);
					this.winTo = acc.nBars;
				}
			}
			this.barsApplied = acc.nBars;
		}
		this.applyMarkers(acc);

		// While not hovering (or hovering the last bar), keep the legend
		// pinned to the newest bar as it streams in.
		if (!this.legendPoint && this.legendHandler) {
			this.emitLegend(this.lastLegendBar(acc), false);
		}
	}

	/** Bulk-load everything accumulated so far and switch to live mode. */
	private finishRender(acc: StreamAccumulator): void {
		this.winTo = acc.nBars;
		this.winFrom = Math.max(0, acc.nBars - PreviewChartAdapter.WINDOW_BARS);
		this.setData(acc, true);
		this.rebuildMarkers(acc);
		this.barsApplied = acc.nBars;
		this.live = true;
	}

	/** (Re)create plot series when meta arrives (once per stream).
	 * Plots start on their own invisible price scale ('plots') and are
	 * re-homed at the first bulk render: price-like plots (values inside
	 * the candle range — MAs/EMAs of an overlay=true script) move to the
	 * candle scale so they align with the candles; oscillator-like plots
	 * (different range, e.g. 0–100 RSI against $4000 bars) stay separate
	 * so they don't flatten the candles. */
	private applyPanes(acc: StreamAccumulator): void {
		if (!this.chart || this.plots.length > 0) return;
		const meta = acc.meta;
		if (!meta) return;
		// Static hlines on the price pane.
		for (const h of meta.hlines) {
			if (h.price > 0) {
				this.candles!.createPriceLine({ price: h.price, color: toCss(h.color), lineStyle: 2, title: h.title });
			}
		}
		meta.plots.forEach((title, i) => {
			this.plots.push(
				this.chart!.addSeries(LineSeries, {
					color: PLOT_COLORS[i % PLOT_COLORS.length],
					lineWidth: 1,
					title,
					priceScaleId: 'plots',
					lineStyle: 0,
					priceLineVisible: false,
					lastValueVisible: false,
					crosshairMarkerVisible: false
				})
			);
		});
		if (this.plots.length > 0) {
			this.chart.priceScale('plots').applyOptions({ visible: false });
		}
	}

	/** One-time decision, run at the first bulk render: move each plot
	 * series whose sampled values sit inside the candle price range onto
	 * the candle price scale (`right`). Must be `'right'` (or `'left'`), not
	 * `''` / a custom id — anything other than left/right is an overlay
	 * scale in LWC, which auto-scales independently so MAs appear to
	 * drift above/below candles when you scroll (TradingView keeps them
	 * locked because overlay=true plots share the price axis). */
	private rehomePlots(acc: StreamAccumulator): void {
		if (this.plotsHomed || this.plots.length === 0 || acc.nBars < 10) return;
		this.plotsHomed = true;
		// Candle price range over the loaded history.
		let lo = Infinity;
		let hi = -Infinity;
		for (let i = 0; i < acc.nBars; i++) {
			const l = acc.bars.low[i];
			const h = acc.bars.high[i];
			if (l < lo) lo = l;
			if (h > hi) hi = h;
		}
		if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
		const span = hi - lo;
		acc.plotColumns.forEach((col, p) => {
			// Sample up to 500 values; count how many are price-like.
			let inRange = 0;
			let total = 0;
			const step = Math.max(1, Math.floor(acc.nBars / 500));
			for (let i = 0; i < acc.nBars; i += step) {
				const v = col[i];
				if (Number.isNaN(v)) continue;
				total++;
				if (v >= lo - span * 0.1 && v <= hi + span * 0.1) inRange++;
			}
			if (total > 0 && inRange / total >= 0.9) {
				this.plots[p].applyOptions({ priceScaleId: 'right' });
			}
		});
	}

	private plotsHomed = false;

	private applyDrawings(acc: StreamAccumulator): void {
		// Lazy-attach on first flush with a live accumulator.
		this.attachDrawings(acc);
		// Repaint when the drawings map gained/lost/changed entries.
		if (this.drawingsPrim && acc.drawingsVersion !== this.drawingsSeen) {
			this.drawingsSeen = acc.drawingsVersion;
			this.drawingsPrim.requestRepaint();
		}
	}

	private drawingsSeen = 0;

	/** Load the window [winFrom, winTo) into the series. `frame` also sets the initial viewport. */
	private setData(acc: StreamAccumulator, frame: boolean): void {
		const bars = acc.bars;
		const to = Math.min(this.winTo, acc.nBars);
		const from = Math.min(this.winFrom, to);
		const n = to - from;
		const candleData: any[] = new Array(n);
		const volData: any[] = new Array(n);
		for (let k = 0; k < n; k++) {
			const i = from + k;
			const t = msToSec(bars.time[i]);
			candleData[k] = { time: t, open: bars.open[i], high: bars.high[i], low: bars.low[i], close: bars.close[i] };
			volData[k] = { time: t, value: bars.volume[i], color: bars.close[i] >= bars.open[i] ? VOLUME_UP : VOLUME_DOWN };
		}
		const chart = this.chart;
		if (!chart) return;
		this.candles!.setData(candleData);
		this.volume!.setData(volData);
		// Plot columns: whitespace for NaN (line break), value otherwise.
		for (let p = 0; p < this.plots.length; p++) {
			const col = acc.plotColumns[p];
			const data: any[] = new Array(n);
			for (let k = 0; k < n; k++) {
				const i = from + k;
				const v = col[i];
				data[k] = Number.isNaN(v) ? { time: msToSec(bars.time[i]) } : { time: msToSec(bars.time[i]), value: v };
			}
			this.plots[p].setData(data);
		}
		// Bulk load is the first point with enough history to decide the
		// plot scale (price-like vs oscillator-like) — re-home plots whose
		// values sit inside the candle range onto the candle scale.
		this.rehomePlots(acc);
		if (frame) this.frameLatest(n);
	}

	/** Show the newest bars: readable candles rather than the whole window squeezed in. */
	private frameLatest(count: number): void {
		const ts = this.chart?.timeScale();
		if (!ts) return;
		if (count <= PreviewChartAdapter.VISIBLE_BARS) {
			ts.fitContent();
			return;
		}
		ts.setVisibleLogicalRange({ from: count - PreviewChartAdapter.VISIBLE_BARS, to: count + 4 });
	}

	private appendBars(acc: StreamAccumulator, from: number, to: number): void {
		const bars = acc.bars;
		for (let i = from; i < to; i++) {
			const t = msToSec(bars.time[i]);
			this.candles!.update({ time: t, open: bars.open[i], high: bars.high[i], low: bars.low[i], close: bars.close[i] });
			this.volume!.update({ time: t, value: bars.volume[i], color: bars.close[i] >= bars.open[i] ? VOLUME_UP : VOLUME_DOWN });
			for (let p = 0; p < this.plots.length; p++) {
				const v = acc.plotColumns[p][i];
				this.plots[p].update(Number.isNaN(v) ? { time: t } : { time: t, value: v });
			}
		}
	}

	/** Live: new fills arrived. Rebuilding the window's markers is cheap. */
	private applyMarkers(acc: StreamAccumulator): void {
		if (!this.markers || this.candlesOnly) return;
		if (acc.fills.length === this.fillsApplied) return;
		this.rebuildMarkers(acc);
	}

	/** Markers for the fills inside the window (fills are in time order). */
	private rebuildMarkers(acc: StreamAccumulator): void {
		this.fillsApplied = acc.fills.length;
		if (!this.markers || this.candlesOnly) return;
		const fills = acc.fills;
		const to = Math.min(this.winTo, acc.nBars);
		if (to <= this.winFrom) {
			this.allMarkers = [];
			this.markers.setMarkers([]);
			return;
		}
		const startMs = acc.bars.time[this.winFrom];
		// Fills on bars past the window's end (when parked on older history) are left out.
		const endMs = to < acc.nBars ? acc.bars.time[to] : Infinity;
		const lowerBound = (ms: number): number => {
			let lo = 0;
			let hi = fills.length;
			while (lo < hi) {
				const mid = (lo + hi) >> 1;
				if (fills[mid].time < ms) lo = mid + 1;
				else hi = mid;
			}
			return lo;
		};
		const first = lowerBound(startMs);
		const last = endMs === Infinity ? fills.length : lowerBound(endMs);
		const out: any[] = new Array(Math.max(0, last - first));
		for (let i = first; i < last; i++) {
			const f = fills[i];
			out[i - first] = {
				time: msToSec(f.time),
				position: f.qty > 0 ? 'belowBar' : 'aboveBar',
				color: f.kind === 'opened' ? (f.qty > 0 ? CANDLE_UP : CANDLE_DOWN) : '#9ca3af',
				shape: f.qty > 0 ? 'arrowUp' : 'arrowDown',
				text: f.kind === 'opened' ? (f.qty > 0 ? 'L' : 'S') : 'X'
			};
		}
		this.allMarkers = out;
		this.markers.setMarkers(out);
	}

	private allMarkers: any[] = [];

	/**
	 * Jump back to the newest bars. Also drops the older chunks pulled in by
	 * panning, so a long trip left doesn't leave a heavy chart behind.
	 */
	scrollToLatest(): void {
		if (!this.chart) return;
		const acc = this.legendAcc;
		if (acc && this.live) {
			const want = Math.max(0, acc.nBars - PreviewChartAdapter.WINDOW_BARS);
			if (this.winFrom !== want || this.winTo !== acc.nBars) {
				this.winFrom = want;
				this.winTo = acc.nBars;
				this.setData(acc, false);
				this.rebuildMarkers(acc);
				this.barsApplied = acc.nBars;
			}
			this.frameLatest(this.winTo - this.winFrom);
			this.drawingsPrim?.requestRepaint();
			return;
		}
		this.chart.timeScale().scrollToRealTime();
	}

	/**
	 * Replace the stream on screen with one that is already loaded (the quick
	 * chart giving way to the full history), keeping the same candles in view.
	 * Both end at the newest candle, so the view is kept as a distance from the
	 * right edge: the zoom and the position stay, whatever the two streams hold.
	 */
	swapStream(acc: StreamAccumulator): void {
		if (!this.chart) return;
		const ts = this.chart.timeScale();
		const before = ts.getVisibleLogicalRange();
		const oldCount = this.winTo - this.winFrom;
		this.resetForNewStream();
		this.apply(acc);
		const count = this.winTo - this.winFrom;
		// Nothing sensible to keep (empty or collapsed view): stay on the default framing.
		if (!before || oldCount <= 0 || count <= 0 || before.to - before.from < 10) return;
		const from = count - (oldCount - before.from);
		const to = count - (oldCount - before.to);
		// The old view reached further back than this window holds.
		if (to - Math.max(from, 0) < 10) return;
		ts.setVisibleLogicalRange({ from, to });
		// Never leave the chart squeezed onto a couple of candles.
		const after = ts.getVisibleLogicalRange();
		if (!after || after.to - after.from < 10) this.frameLatest(count);
	}

	/** Clear series/cursors for a new stream without destroying the chart host. */
	resetForNewStream(): void {
		if (this.drawingsPrim && this.candles) {
			try {
				this.candles.detachPrimitive(this.drawingsPrim);
			} catch {
				/* ignore */
			}
			this.drawingsPrim = null;
		}
		try {
			this.candles?.setData([]);
			this.volume?.setData([]);
			for (const p of this.plots) p.setData([]);
			this.markers?.setMarkers([]);
		} catch {
			/* chart may be mid-teardown */
		}
		// Drop plot series so meta from the next stream re-creates them.
		for (const p of this.plots) {
			try {
				this.chart?.removeSeries(p);
			} catch {
				/* ignore */
			}
		}
		this.plots = [];
		this.barsApplied = 0;
		this.fillsApplied = 0;
		this.allMarkers = [];
		this.winFrom = 0;
		this.winTo = 0;
		this.sliding = false;
		this.live = false;
		this.markersById.clear();
		this.legendAcc = null;
		this.legendPoint = null;
		this.plotsHomed = false;
		this.drawingsSeen = 0;
	}

	/** Remove the chart and free listeners. */
	destroy(): void {
		if (this.drawingsPrim && this.candles) {
			try {
				this.candles.detachPrimitive(this.drawingsPrim);
			} catch {
				// chart already removed
			}
			this.drawingsPrim = null;
		}
		if (this.chart) {
			this.chart.remove();
			this.chart = null;
		}
		this.candles = null;
		this.volume = null;
		this.equity = null;
		this.plots = [];
		this.markers = null;
		this.barsApplied = 0;
		this.fillsApplied = 0;
		this.allMarkers = [];
		this.winFrom = 0;
		this.winTo = 0;
		this.sliding = false;
		this.live = false;
		this.markersById.clear();
		this.legendAcc = null;
		this.legendPoint = null;
		this.plotsHomed = false;
		// crosshairBound stays false-able via chart removal: subscribe uses
		// the fresh chart instance created in mount(), so rebind there.
		this.crosshairBound = false;
		this.rangeBound = false;
	}
}

/** Wire RGBA u32 → CSS color string. */
function toCss(rgba: number): string {
	const a = rgba & 0xff;
	const b = (rgba >>> 8) & 0xff;
	const g = (rgba >>> 16) & 0xff;
	const r = (rgba >>> 24) & 0xff;
	return `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`;
}
